// frontend/src/modules/hr/pages/PerformancePage.tsx
//
// HR-facing Performance page: goals + reviews for the team, filterable by
// employee. Reads are allowed for hr.performance.manage OR
// hr.performance.view_all (server decides whether drafts are included —
// view_all-only holders never see a draft review). Writes (goal CRUD,
// review draft CRUD, share) are gated client-side on hr.performance.manage
// to hide affordances a view_all-only user's POST/PATCH/DELETE would 403 on.

import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Target, ClipboardList } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useApi } from '../../../design-system/useApi';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { usePermission } from '../../../core/auth/usePermission';
import { VISUAL, statusPill } from '../../../design-system/visual';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { hrApi } from '../api';
import type { EmployeeWithUser, PerformanceGoal, PerformanceGoalStatus, PerformanceReview } from '../types';
import { GoalDialog } from '../components/GoalDialog';
import { ReviewDialog } from '../components/ReviewDialog';

function goalStatusTone(status: PerformanceGoalStatus): 'info' | 'success' | 'muted' {
  if (status === 'achieved') return 'success';
  if (status === 'dropped') return 'muted';
  return 'info';
}

function reviewStatusTone(status: PerformanceReview['status']): 'draft' | 'info' | 'success' {
  if (status === 'shared') return 'info';
  if (status === 'acknowledged') return 'success';
  return 'draft';
}

export function PerformancePage() {
  const canManage = usePermission('hr.performance.manage');
  const confirm = useConfirm();

  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = employeesQ.data?.employees ?? [];
  const [employeeId, setEmployeeId] = React.useState<string>('all');
  const employeeName = employees.find((e) => e.id === employeeId)?.name;

  const goalsQ = useApi<{ goals: PerformanceGoal[] }>(
    `/api/hr/performance/goals${employeeId !== 'all' ? `?employee_id=${employeeId}` : ''}`,
    [employeeId],
  );
  const reviewsQ = useApi<{ reviews: PerformanceReview[] }>(
    `/api/hr/performance/reviews${employeeId !== 'all' ? `?employee_id=${employeeId}` : ''}`,
    [employeeId],
  );

  const [goalDialog, setGoalDialog] = React.useState<{ open: boolean; goal?: PerformanceGoal | null }>({ open: false });
  const [reviewDialog, setReviewDialog] = React.useState<{ open: boolean; review?: PerformanceReview | null }>({ open: false });

  function nameFor(empId: string): string {
    return employees.find((e) => e.id === empId)?.name ?? '—';
  }

  async function deleteGoal(goal: PerformanceGoal) {
    if (!(await confirm({ title: 'Delete this goal?', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await hrApi.deleteGoal(goal.id);
      toast.success('Goal deleted');
      goalsQ.reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  async function deleteReview(review: PerformanceReview) {
    if (!(await confirm({ title: 'Delete this draft?', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await hrApi.deleteReviewDraft(review.id);
      toast.success('Draft deleted');
      reviewsQ.reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  async function share(review: PerformanceReview) {
    const empName = nameFor(review.employee_id);
    if (!(await confirm({
      title: `Share with ${empName}?`,
      description: "They'll be notified and the review becomes final.",
      confirmLabel: 'Share',
    }))) return;
    try {
      await hrApi.shareReview(review.id);
      toast.success('Review shared');
      reviewsQ.reload();
    } catch (err: any) { toast.error('Could not share', { description: err.message }); }
  }

  const readForbidden = goalsQ.status === 403 || reviewsQ.status === 403;

  return (
    <div>
      <PageHeader
        eyebrow="Team / Performance"
        title="Performance"
        description="Goals and reviews for your team — shared reviews notify the employee and become read-only."
        actions={
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {readForbidden ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Performance is visible to workspace owners and HR managers." />
          </section>
        ) : (
          <Tabs defaultValue="goals">
            <TabsList className="mb-6">
              <TabsTrigger value="goals">Goals</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
            </TabsList>

            <TabsContent value="goals" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={VISUAL.dashSection}>Goals</h3>
                {canManage && (
                  <Button
                    size="sm"
                    disabled={employeeId === 'all'}
                    onClick={() => setGoalDialog({ open: true, goal: null })}
                    className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New goal
                  </Button>
                )}
              </div>
              {canManage && employeeId === 'all' && (
                <p className="text-[12.5px] text-muted-foreground">Pick an employee above to add a goal.</p>
              )}

              {goalsQ.error ? (
                <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <ErrorState title="Couldn't load goals" description={goalsQ.error} onRetry={goalsQ.reload} />
                </section>
              ) : (
                <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable<PerformanceGoal>
                    loading={goalsQ.loading}
                    rows={goalsQ.data?.goals ?? []}
                    rowKey={(g) => g.id}
                    columns={[
                      { key: 'title', header: 'Title', cell: (g) => <span className="font-medium text-foreground">{g.title}</span> },
                      ...(employeeId === 'all'
                        ? [{ key: 'employee', header: 'Employee', cell: (g: PerformanceGoal) => nameFor(g.employee_id) }]
                        : []),
                      { key: 'due', header: 'Due', cell: (g) => g.due_date ?? '—' },
                      { key: 'status', header: 'Status', cell: (g) => <span className={statusPill(goalStatusTone(g.status))}>{g.status}</span> },
                      ...(canManage ? [{
                        key: 'actions', header: '', cell: (g: PerformanceGoal) => (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setGoalDialog({ open: true, goal: g })}>Edit</Button>
                            <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => deleteGoal(g)}>Delete</Button>
                          </div>
                        ),
                      }] : []),
                    ]}
                    emptyState={<EmptyState icon={<Target />} title="No goals yet" description="Add a goal above to start tracking progress." />}
                  />
                </section>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={VISUAL.dashSection}>Reviews</h3>
                {canManage && (
                  <Button
                    size="sm"
                    disabled={employeeId === 'all'}
                    onClick={() => setReviewDialog({ open: true, review: null })}
                    className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New review draft
                  </Button>
                )}
              </div>
              {canManage && employeeId === 'all' && (
                <p className="text-[12.5px] text-muted-foreground">Pick an employee above to draft a review.</p>
              )}

              {reviewsQ.error ? (
                <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <ErrorState title="Couldn't load reviews" description={reviewsQ.error} onRetry={reviewsQ.reload} />
                </section>
              ) : (
                <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable<PerformanceReview>
                    loading={reviewsQ.loading}
                    rows={reviewsQ.data?.reviews ?? []}
                    rowKey={(r) => r.id}
                    columns={[
                      ...(employeeId === 'all'
                        ? [{ key: 'employee', header: 'Employee', cell: (r: PerformanceReview) => <span className="font-medium text-foreground">{nameFor(r.employee_id)}</span> }]
                        : []),
                      { key: 'period', header: 'Period', cell: (r) => `${r.period_start} – ${r.period_end}` },
                      { key: 'rating', header: 'Rating', cell: (r) => <span className="font-mono tabular-nums">{r.overall_rating} / 5</span> },
                      { key: 'status', header: 'Status', cell: (r) => <span className={statusPill(reviewStatusTone(r.status))}>{r.status}</span> },
                      ...(canManage ? [{
                        key: 'actions', header: '', cell: (r: PerformanceReview) => (
                          <div className="flex gap-1 justify-end">
                            {r.status === 'draft' ? (
                              <>
                                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setReviewDialog({ open: true, review: r })}>Edit</Button>
                                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => share(r)}>Share</Button>
                                <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => deleteReview(r)}>Delete</Button>
                              </>
                            ) : (
                              <span className="text-[12px] text-muted-foreground">Read-only</span>
                            )}
                          </div>
                        ),
                      }] : []),
                    ]}
                    emptyState={<EmptyState icon={<ClipboardList />} title="No reviews yet" description="Draft a review above once a period is ready to evaluate." />}
                  />
                </section>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {canManage && (
        <>
          <GoalDialog
            open={goalDialog.open}
            onOpenChange={(v) => setGoalDialog((s) => ({ ...s, open: v }))}
            goal={goalDialog.goal}
            employeeId={goalDialog.goal ? undefined : (employeeId !== 'all' ? employeeId : undefined)}
            employeeName={goalDialog.goal ? undefined : employeeName}
            onSaved={() => { setGoalDialog({ open: false }); goalsQ.reload(); }}
          />
          <ReviewDialog
            open={reviewDialog.open}
            onOpenChange={(v) => setReviewDialog((s) => ({ ...s, open: v }))}
            review={reviewDialog.review}
            employeeId={reviewDialog.review ? undefined : (employeeId !== 'all' ? employeeId : undefined)}
            employeeName={reviewDialog.review ? undefined : employeeName}
            onSaved={() => { setReviewDialog({ open: false }); reviewsQ.reload(); }}
          />
        </>
      )}
    </div>
  );
}
